import { useState } from 'react';

interface Category {
  id: string;
  name: string;
  nameEn?: string;
  badge?: string;
  badgeColor?: string;
  originalPrice: number;
  discountedPrice: number;
  description?: string;
}

interface ShippingOption {
  id: string;
  label: string;
  labelEn?: string;
  price: number;
}

interface RaceSelectionSettingsData {
  categories?: Category[];
  shippingOptions?: ShippingOption[];
  displayStyle?: 'cards' | 'list' | 'grid';
  showOriginalPrice?: boolean;
  showDiscount?: boolean;
  discountLabel?: string;
}

interface Props {
  settings: RaceSelectionSettingsData;
  onChange: (settings: RaceSelectionSettingsData) => void;
}

export default function RaceSelectionSettings({ settings, onChange }: Props) {
  const [activeSection, setActiveSection] = useState<'categories' | 'shipping' | 'display'>('categories');

  const categories = settings.categories || [];
  const shippingOptions = settings.shippingOptions || [];

  function addCategory() {
    const newCategory: Category = {
      id: `cat_${Date.now()}`,
      name: '新組別',
      nameEn: '',
      originalPrice: 0,
      discountedPrice: 0,
    };
    onChange({ ...settings, categories: [...categories, newCategory] });
  }

  function updateCategory(id: string, updates: Partial<Category>) {
    onChange({
      ...settings,
      categories: categories.map(c => c.id === id ? { ...c, ...updates } : c),
    });
  }

  function removeCategory(id: string) {
    onChange({
      ...settings,
      categories: categories.filter(c => c.id !== id),
    });
  }

  function addShippingOption() {
    const newOption: ShippingOption = {
      id: `ship_${Date.now()}`,
      label: '新取件方式',
      labelEn: '',
      price: 0,
    };
    onChange({ ...settings, shippingOptions: [...shippingOptions, newOption] });
  }

  function updateShippingOption(id: string, updates: Partial<ShippingOption>) {
    onChange({
      ...settings,
      shippingOptions: shippingOptions.map(s => s.id === id ? { ...s, ...updates } : s),
    });
  }

  function removeShippingOption(id: string) {
    onChange({
      ...settings,
      shippingOptions: shippingOptions.filter(s => s.id !== id),
    });
  }

  return (
    <div className="space-y-4">
      {/* Section Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
        <button
          onClick={() => setActiveSection('categories')}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeSection === 'categories' ? 'bg-white shadow text-primary' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          組別 ({categories.length})
        </button>
        <button
          onClick={() => setActiveSection('shipping')}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeSection === 'shipping' ? 'bg-white shadow text-primary' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          取件 ({shippingOptions.length})
        </button>
        <button
          onClick={() => setActiveSection('display')}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeSection === 'display' ? 'bg-white shadow text-primary' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          顯示
        </button>
      </div>

      {/* Categories Section */}
      {activeSection === 'categories' && (
        <div className="space-y-3">
          {categories.map((cat, idx) => (
            <div key={cat.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500">組別 {idx + 1}</span>
                <button
                  onClick={() => removeCategory(cat.id)}
                  className="text-red-500 hover:text-red-600 text-xs"
                >
                  刪除
                </button>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={cat.name}
                    onChange={(e) => updateCategory(cat.id, { name: e.target.value })}
                    placeholder="名稱"
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                  />
                  <input
                    type="text"
                    value={cat.nameEn || ''}
                    onChange={(e) => updateCategory(cat.id, { nameEn: e.target.value })}
                    placeholder="英文名稱"
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">原價</label>
                    <input
                      type="number"
                      value={cat.originalPrice || 0}
                      onChange={(e) => updateCategory(cat.id, { originalPrice: Number(e.target.value) })}
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">團報價</label>
                    <input
                      type="number"
                      value={cat.discountedPrice || 0}
                      onChange={(e) => updateCategory(cat.id, { discountedPrice: Number(e.target.value) })}
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={cat.badge || ''}
                    onChange={(e) => updateCategory(cat.id, { badge: e.target.value })}
                    placeholder="標籤 (例: 熱門)"
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                  />
                  <input
                    type="text"
                    value={cat.badgeColor || ''}
                    onChange={(e) => updateCategory(cat.id, { badgeColor: e.target.value })}
                    placeholder="標籤顏色 (#FF0000)"
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                  />
                </div>
                <textarea
                  value={cat.description || ''}
                  onChange={(e) => updateCategory(cat.id, { description: e.target.value })}
                  placeholder="說明文字"
                  rows={2}
                  className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                />
              </div>
            </div>
          ))}
          <button
            onClick={addCategory}
            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-primary hover:text-primary transition-colors"
          >
            + 新增組別
          </button>
        </div>
      )}

      {/* Shipping Section */}
      {activeSection === 'shipping' && (
        <div className="space-y-3">
          {shippingOptions.map((opt, idx) => (
            <div key={opt.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500">取件方式 {idx + 1}</span>
                <button
                  onClick={() => removeShippingOption(opt.id)}
                  className="text-red-500 hover:text-red-600 text-xs"
                >
                  刪除
                </button>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={opt.label}
                    onChange={(e) => updateShippingOption(opt.id, { label: e.target.value })}
                    placeholder="名稱"
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                  />
                  <input
                    type="text"
                    value={opt.labelEn || ''}
                    onChange={(e) => updateShippingOption(opt.id, { labelEn: e.target.value })}
                    placeholder="英文名稱"
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">費用</label>
                  <input
                    type="number"
                    value={opt.price || 0}
                    onChange={(e) => updateShippingOption(opt.id, { price: Number(e.target.value) })}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
            </div>
          ))}
          <button
            onClick={addShippingOption}
            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-primary hover:text-primary transition-colors"
          >
            + 新增取件方式
          </button>
        </div>
      )}

      {/* Display Section */}
      {activeSection === 'display' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">顯示樣式</label>
            <div className="grid grid-cols-3 gap-2">
              {(['cards', 'list', 'grid'] as const).map((style) => (
                <button
                  key={style}
                  onClick={() => onChange({ ...settings, displayStyle: style })}
                  className={`px-3 py-2 text-xs border rounded-lg transition-colors ${
                    settings.displayStyle === style
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {style === 'cards' ? '卡片' : style === 'list' ? '列表' : '網格'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm">顯示原價</span>
            <button
              onClick={() => onChange({ ...settings, showOriginalPrice: !settings.showOriginalPrice })}
              className={`w-10 h-5 rounded-full transition-colors relative ${
                settings.showOriginalPrice ? 'bg-primary' : 'bg-gray-300'
              }`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                settings.showOriginalPrice ? 'left-5' : 'left-0.5'
              }`} />
            </button>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm">顯示折扣</span>
            <button
              onClick={() => onChange({ ...settings, showDiscount: !settings.showDiscount })}
              className={`w-10 h-5 rounded-full transition-colors relative ${
                settings.showDiscount ? 'bg-primary' : 'bg-gray-300'
              }`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                settings.showDiscount ? 'left-5' : 'left-0.5'
              }`} />
            </button>
          </div>

          {settings.showDiscount && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">折扣標籤</label>
              <input
                type="text"
                value={settings.discountLabel || ''}
                onChange={(e) => onChange({ ...settings, discountLabel: e.target.value })}
                placeholder="例: 早鳥優惠"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
